/* =========================================================================
 * RELATED OPPORTUNITIES — the thread between the tabs.
 *
 * Brian: "integration between every single tab is Paramount."
 *
 * ⚠️ THE TABS WERE THE PROBLEM, NOT THE ANSWER. Splitting Bowl Season and
 * Postseason Runs into their own spaces made each one legible and, on its own,
 * made the platform WORSE at the thing it exists for: one school's season is
 * now scattered across three tabs that cannot see each other. A rep looking at
 * the FCS Semifinals had no way to know the FCS Championship is a record too,
 * or that the same school has a bowl opportunity.
 *
 * So the tabs are a LENS, and this module is the thread running underneath them.
 * It is pure and takes the full universe as an argument — the Command Center
 * already holds every record in memory in every space (`championships` on
 * `useCategoryUniverse`), so relating them costs one pass and zero requests.
 *
 * ⚠️ IT RELATES, IT DOES NOT MERGE. Every related record keeps its own id and
 * its own space; the drawer LINKS to it. Nothing here rewrites a record, and
 * nothing invents a relationship that isn't derivable from fields both records
 * already carry — a guessed parent is worse than no link, because a rep would
 * work the wrong title.
 * ========================================================================= */

const S = (v) => String(v ?? '').trim()

/** Which space does this record live in? Mirrors `recordFilter` in categories.js
 *  — deliberately the same two structural markers, so a record can never be
 *  linked to a space that would filter it out. */
export function spaceOf(record) {
  const r = record?.raw || record || {}
  const level = S(r.sr_level ?? record?.level)
  const segment = S(r.sr_segment ?? record?.segment)
  if (level === 'Tournament Round') return 'postseason'
  if (segment === 'Bowl') return 'bowls'
  return 'championships'
}

export const SPACE_LABEL = {
  championships: 'Championships',
  bowls: 'Bowl Season',
  postseason: 'Postseason Runs',
}

/**
 * The identity a round shares with its title: sport + gender + division.
 *
 * ⚠️ THE DIVISION IS PART OF THE MATCH and that is not optional. Division II
 * football's quarterfinal belongs to Division II's championship, not Division
 * III's. The catalog itself proved how easy that collapse is — three divisions'
 * rounds once produced one identical record because the division was dropped.
 */
/**
 * The NCAA division, as a canonical token, derived from WHEREVER it is stated.
 *
 * ⚠️ THIS READS THE NAME, and that is the fix for a real defect. `sr_division`
 * is empty on these records — the division is carried in the name, because the
 * catalog builds a name out of sport + gender + division + round. Reading only
 * the field meant every football round had division `''`, so DI-FCS, DII and
 * DIII all matched each other and a Semifinal offered eleven "siblings" from
 * three different divisions. It is the same mistake as the one that produced
 * the duplicate keys, one layer up: the division was present, just not in the
 * place the code looked.
 *
 * Longest form first — "Division I FCS" must win before "Division I" matches
 * its prefix, or every subdivision collapses back into DI.
 */
const DIVISIONS = [
  [/division\s*i{1,3}\s*fcs|division\s*1\s*fcs|\bfcs\b/i, 'di-fcs'],
  [/division\s*i{1,3}\s*fbs|division\s*1\s*fbs|\bfbs\b/i, 'di-fbs'],
  [/division\s*iii\b|division\s*3\b|\bdiii\b|\bd-?iii\b/i, 'diii'],
  [/division\s*ii\b|division\s*2\b|\bdii\b|\bd-?ii\b/i, 'dii'],
  [/division\s*i\b|division\s*1\b|\bdi\b|\bd-?i\b/i, 'di'],
]

function divisionOf(record) {
  const r = record?.raw || record || {}
  const hay = `${S(r.sr_division ?? record?.division)} ${S(record?.name ?? r.championship_name)}`
  for (const [re, token] of DIVISIONS) if (re.test(hay)) return token
  return ''
}

function lineage(record) {
  const r = record?.raw || record || {}
  const sport = S(r.sr_sport ?? record?.sport).toLowerCase()
  const gender = S(r.sr_gender ?? record?.gender).toLowerCase()
  if (!sport) return null
  return { sport, gender, division: divisionOf(record) }
}

const sameLineage = (a, b) =>
  a && b && a.sport === b.sport && a.gender === b.gender && a.division === b.division

/**
 * Everything connected to this record, grouped by the space it lives in.
 *
 * Returns `{ title, rounds, siblings }`:
 *   · `title`    — the championship this run leads to (null from a title itself)
 *   · `rounds`   — the postseason rounds beneath a title, LARGEST FIELD FIRST,
 *                  because that is the order a season actually happens in and
 *                  the order of descending customer count
 *   · `siblings` — the other rounds of the same tournament, seen from a round
 *
 * ⚠️ CAPPED AND SORTED, never "everything that matched". An unbounded related
 * list on a 14k-record universe is how a drawer becomes unopenable.
 */
export function relatedFor(record, universe, { limit = 12 } = {}) {
  const empty = { title: null, rounds: [], siblings: [], space: spaceOf(record) }
  if (!record || !Array.isArray(universe) || !universe.length) return empty

  const mine = lineage(record)
  if (!mine) return empty
  const id = S(record.id)
  const space = spaceOf(record)

  const kin = universe.filter((c) => S(c.id) !== id && sameLineage(lineage(c), mine))
  if (!kin.length) return empty

  /* The team count is parsed back out of the label rather than stored twice.
   * One source for a number beats two that can disagree. */
  const teams = (c) => {
    const m = /\((\d+)\s+teams?\)/i.exec(S(c.name ?? c?.raw?.championship_name))
    return m ? Number(m[1]) : 0
  }
  const byField = (a, b) => teams(b) - teams(a)

  const rounds = kin.filter((c) => spaceOf(c) === 'postseason').sort(byField)
  const titles = kin.filter((c) => spaceOf(c) !== 'postseason')

  if (space === 'postseason') {
    return {
      space,
      /* A round's title is the championship record of the same lineage. If more
       * than one matches we take NONE rather than the first: two candidate
       * titles is an ambiguity to show, not a coin to flip. */
      title: titles.length === 1 ? titles[0] : null,
      ambiguousTitle: titles.length > 1 ? titles : null,
      rounds: [],
      siblings: rounds.slice(0, limit),
    }
  }

  return { space, title: null, rounds: rounds.slice(0, limit), siblings: [] }
}

/** How many schools this record's whole postseason reaches — the number that
 *  says why a round is worth working at all. Counts the record itself when it
 *  is a round, so a Sweet 16 does not report 16 as though it were the total. */
export function reachOf(related, record) {
  const all = [...(related.rounds || []), ...(related.siblings || []), record]
  const seen = new Set()
  let max = 0
  for (const c of all) {
    const m = /\((\d+)\s+teams?\)/i.exec(S(c?.name ?? c?.raw?.championship_name))
    if (!m) continue
    const n = Number(m[1])
    const k = `${S(c.id)}`
    if (seen.has(k)) continue
    seen.add(k)
    if (n > max) max = n
  }
  return max
}
